import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@material-ui/core";
import { Close, CheckCircleOutline, HighlightOff } from "@material-ui/icons";
import { LoadingButton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/styles";
import { useSnackbar } from "src/components/snackbar";
import { propuestasApi } from "src/services/propuestasApi";
import { formatEstadoPropuesta } from "src/utils/propuestasV2/formatEstadoPropuesta";
import {
  derivarDecisionesPorItem,
  contarAceptadosVigentes,
  anticiparEstadoCierre,
} from "src/utils/propuestasV2/decisionesRenegociacionV2";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
}));

const MOTIVO_MAX_LENGTH = 500;

/**
 * Cierre explícito de una propuesta en EN_RENEGOCIACION: la única salida a un
 * estado terminal cuando quedó algún servicio observado, sin esperar a que
 * caduque. No materializa nada — los servicios aceptados ya se activaron
 * cuando el cliente los aceptó — solo deriva el desenlace de las decisiones
 * ya registradas.
 */
export default function ModalCierreExplicitoV2({
  open,
  setOpen,
  propuesta,
  onSuccess,
  maxWidth = "sm",
  fullWidth = true,
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [cerrarPropuesta] = propuestasApi.useCerrarPropuestaV2Mutation();

  const items = useMemo(
    () => (Array.isArray(propuesta?.items) ? propuesta.items : []),
    [propuesta],
  );

  // La decisión vigente de cada ítem viaja en `item.respuesta` (detalle de la
  // propuesta) — no se deriva del hilo, que conserva en su bitácora
  // decisiones que la ronda siguiente ya borró de propuesta_item_respuesta.
  const decisionesPorItem = useMemo(
    () => derivarDecisionesPorItem(items),
    [items],
  );

  const { aceptados, noAceptados, total } = useMemo(
    () => contarAceptadosVigentes(items, decisionesPorItem),
    [items, decisionesPorItem],
  );

  // De los no aceptados, cuántos son porque el cliente nunca alcanzó a
  // responder (ni aceptó, ni rechazó, ni observó) — es el caso que sorprende
  // al vendedor si no se anticipa antes de confirmar.
  const sinRespuesta = useMemo(
    () => items.filter((item) => !decisionesPorItem.has(item.id)).length,
    [items, decisionesPorItem],
  );

  const estadoAnticipado = anticiparEstadoCierre(aceptados, total);

  const [motivo, setMotivo] = useState("");
  const [procesando, setProcesando] = useState(false);

  const handleClose = () => {
    if (!procesando) {
      setMotivo("");
      setOpen(false);
    }
  };

  const handleConfirmar = async () => {
    setProcesando(true);
    try {
      const body = {};
      if (motivo.trim()) body.motivo = motivo.trim();
      const resultado = await cerrarPropuesta({
        id: propuesta.id,
        body,
      }).unwrap();
      enqueueSnackbar(
        `Propuesta cerrada: ${formatEstadoPropuesta(resultado.estado)}.`,
        { variant: "success" },
      );
      setMotivo("");
      setOpen(false);
      onSuccess?.();
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message ||
          error?.message ||
          "Ocurrió un error al cerrar la propuesta.",
        { variant: "error" },
      );
    } finally {
      setProcesando(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth={maxWidth}
      onClose={handleClose}
      fullWidth={fullWidth}
    >
      <DialogTitle className={classes.dialogTitle}>
        Cerrar propuesta — #{propuesta?.id}
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          disabled={procesando}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Cerrar la propuesta es la salida definitiva de la conversación: no
            hay una ronda más. Lo que no quedó aceptado —incluyendo lo que el
            cliente todavía no alcanzó a revisar— cuenta como no aceptado.
          </Typography>
        </Alert>

        <Stack spacing={1} sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleOutline fontSize="small" color="success" />
            <Typography variant="body2">
              <strong>{aceptados}</strong> de <strong>{total}</strong> servicios
              quedan aceptados.
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <HighlightOff fontSize="small" color="error" />
            <Typography variant="body2">
              <strong>{noAceptados}</strong> quedan como no aceptados.
            </Typography>
          </Stack>
          {sinRespuesta > 0 && (
            <Typography variant="caption" color="textSecondary" sx={{ pl: 4 }}>
              De esos, {sinRespuesta} nunca recibieron respuesta del cliente.
            </Typography>
          )}
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Con esto, la propuesta quedaría en{" "}
            <strong>{formatEstadoPropuesta(estadoAnticipado)}</strong>. El
            estado final lo confirma el servidor al cerrar.
          </Typography>
        </Alert>

        <Divider sx={{ mb: 2 }} />

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Motivo del cierre (opcional)"
          placeholder="Ej: El cliente no respondió dentro del plazo acordado por teléfono..."
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          disabled={procesando}
          variant="outlined"
          inputProps={{ maxLength: MOTIVO_MAX_LENGTH }}
          helperText={`${motivo.length}/${MOTIVO_MAX_LENGTH} caracteres`}
        />
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={handleClose} disabled={procesando}>
          Cancelar
        </Button>
        <LoadingButton
          variant="contained"
          color="warning"
          loading={procesando}
          disabled={procesando}
          onClick={handleConfirmar}
        >
          Cerrar propuesta
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

ModalCierreExplicitoV2.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  propuesta: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  maxWidth: PropTypes.string,
  fullWidth: PropTypes.bool,
};

ModalCierreExplicitoV2.defaultProps = {
  onSuccess: undefined,
  maxWidth: "sm",
  fullWidth: true,
};
